<template>
  <BField>
    <BAutocomplete
      ref="input"
      :data="invoices"
      placeholder="UUID"
      field="uuid"
      :value="invoiceId"
      :loading="loading"
      expanded
      open-on-focus
      @select="option => selected = option"
      @typing="searchPendingInvoices"
    >
      <template slot="empty">
        No se encontraron facturas con este criterio.
      </template>
      <template slot-scope="props">
        <div class="media">
          <div class="media-left">
            Folio: {{ props.option.folio }}
          </div>
          <div class="media-content">
            Total: {{ total(props.option.cfdi) }} <br>
            Nombre: {{ props.option.receiverName }} <br>
            RFC: {{ props.option.receiverRfc }} <br>
            <small>
              UUID: {{ props.option.uuid }}
            </small>
          </div>
        </div>
      </template>
    </BAutocomplete>
    <BModal
      :active.sync="modal"
      has-modal-card
    >
      <Pusher v-bind="pusherData" />
    </BModal>
  </BField>
</template>

<script>
import merge from 'lodash/merge';

import { mapState, mapMutations, mapActions } from 'vuex';
import utils from '../../../../lib/utils';
import paymentsHelper from '../../../../lib/paymentsHelper';

const fields = {
  name: 'receiverName',
  rfc: 'receiverRfc',
  folio: 'folio',
  uuid: 'uuid',
};

export default {
  name: 'NewDocumentRelatedSearch',
  components: {
    Pusher: () => import(/* webpackChunkName: "new-credit-note-push" */ '@/components/Admin/Invoices/NewCreditNotePush.vue'),
  },
  props: {
    index: {
      type: Number,
      default: 0,
    },
    invoiceId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      text: '',
      rfc: null,
      loading: false,
      invoice: null,
      selected: null,
      modal: false,
      name: null,
      uuid: null,
      filterInvoices: '',
    };
  },
  computed: merge(
    mapState({
      plainInvoices: ({ invoices }) => invoices.list,
      receiverName: ({ invoice }) => invoice.receiverName,
      receiverRfc: ({ invoice }) => invoice.receiverRfc,
      cfdisRelated: ({ invoice }) => invoice.cfdisRelated,
      invoiceStamp: ({ invoice }) => invoice.complement.digitalTaxStamp,
    }),
    {
      currentRelated() {
        return this.$store.state.invoice.complement.payments[0].reduce(
          (accum, { documentId }) => accum.concat(documentId), [],
        );
      },
      invoices() {
        const { payments } = this.$store.state.invoice.complement;
        const { plainInvoices } = this;

        if (!payments) {
          return plainInvoices;
        }

        const currentUuids = payments[0].documentsRelated
          .reduce((accum, { documentId }) => {
            if (documentId) {
              accum.push(documentId);
            }

            return accum;
          }, []);

        if (this.invoiceStamp) {
          return [];
        }

        return plainInvoices.filter(
          ({ cfdi }) => {
            const { uuid } = cfdi.complement.digitalTaxStamp;

            const existUuid = !currentUuids.includes(uuid);

            if (!existUuid) {
              return false;
            }

            if (this.filterInvoices) {
              return (uuid.indexOf(this.filterInvoices) > -1);
            }

            return true;
          },
        );
      },
      pusherData() {
        if (this.selected) {
          const invoiceSelected = this.selected;
          this.$emit('selected', invoiceSelected, this.index);
        }

        return this.selected ? this.selected.cfdi : {};
      },
      field() {
        return fields[this.fields];
      },
      total: () => ({ voucherTotal, voucherCurrency }) => (
        utils.toCurrency(voucherTotal, voucherCurrency)
      ),
    },
  ),
  methods: merge(
    mapActions({ search: 'invoices/search' }),
    mapMutations({ setValue: 'invoice/setValue' }),
    {
      init() {
        this.rfc = null;
        this.name = null;
        this.selected = null;
        this.uuid = null;
      },
      open() {
        this.modal = true;
      },
      close() {
        this.modal = false;
        this.init();
      },
      blank() {
        this.setValue({ accessor: 'voucherType', value: 'E' });
        this.setValue({ accessor: 'receiverName', value: this.selected.receiverName });
        this.setValue({ accessor: 'receiverRfc', value: this.selected.receiverRfc });

        this.setValue({
          accessor: 'concepts.[0].productServiceKey',
          value: '84111506',
        });

        this.setValue({
          accessor: 'concepts.[0].unitKey',
          value: 'ACT',
        });

        this.setValue({
          accessor: 'concepts.[0].quantity',
          value: 1,
        });

        this.setValue({
          accessor: 'concepts.[0].unit',
          value: 0,
        });
      },
      push() {
        if (!this.selected) return;

        this.open();

        if (this.cfdisRelated.length === 0) this.blank();
      },
      searchPendingInvoices(value) {
        this.filterInvoices = value;
      },
      onSelect(event) {
        const index = this.index + 1;
        const { cfdi } = event.cfdi;
        const { uuid } = cfdi.complement.digitalTaxStamp;

        const { totalAmount = 0, voucherTotal } = cfdi;

        const partiality = (cfdi.operation.payments.length + 1) || 1;
        const documentPreviousBalance = voucherTotal - totalAmount;

        this.searchPendingInvoices('');

        this.setDocumentValue({
          accessor: 'documentId',
          value: uuid,
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentSeries',
          value: cfdi.voucherSeries,
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentConsecutive',
          value: cfdi.voucherConsecutive,
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentPartialityNumber',
          value: partiality,
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentPreviousBalance',
          value: utils.fixTotals(documentPreviousBalance, 'MXN', 2),
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentCurrency',
          value: cfdi.voucherCurrency,
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentInvoiceExchangeRate',
          value: cfdi.voucherExchangeRate,
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentTotal',
          value: cfdi.voucherTotal,
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'documentTaxTransfers',
          value: paymentsHelper.createTransfersDocumentRelated(cfdi.concepts),
          index,
          payment: 1,
        });

        this.setDocumentValue({
          accessor: 'objectImpDr',
          value: paymentsHelper.setObjectImpDr(cfdi.concepts),
          index,
          payment: 1,
        });

        if (cfdi.taxes && cfdi.taxes.retained) {
          this.setDocumentValue({
            accessor: 'documentTaxWithholdings',
            value: paymentsHelper.createRetainedDocumentRelated(cfdi.concepts),
            index,
            payment: 1,
          });
        }

        this.$store.commit('invoice/setValue', {
          accessor: 'complement.payments[0].currency',
          value: cfdi.voucherCurrency,
        });
      },
    },
  ),
};
</script>
